import { useState } from "react";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { PORTAL_API, portalAxios } from "./PortalApp";

const errText = (e, fb) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : fb; };
const hdr = () => (sessionStorage.getItem("portal_token") ? { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` } : {});

export function PasswordField({ value, onChange, placeholder, testId, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoComplete={autoComplete}
        className="w-full h-11 rounded-lg bg-white/10 border border-white/15 px-3 pr-10 text-sm text-white placeholder:text-white/40 outline-none focus:border-emerald-300" data-testid={testId} />
      <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-3 text-white/50 hover:text-white" tabIndex={-1} data-testid={`${testId}-toggle`}>{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
    </div>
  );
}

export function PasswordLoginForm({ onDone }) {
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !pwd) { setErr("Enter your e-mail and password"); return; }
    setBusy(true); setErr("");
    try {
      const r = await portalAxios.post(`${PORTAL_API}/auth/login`, { email: email.trim(), password: pwd });
      sessionStorage.setItem("portal_token", r.data.session_token || "");
      onDone(r.data);
    } catch (ex) { setErr(errText(ex, "Sign-in failed")); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="portal-password-form">
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail" autoComplete="username"
        className="w-full h-11 rounded-lg bg-white/10 border border-white/15 px-3 text-sm text-white placeholder:text-white/40 outline-none focus:border-emerald-300" data-testid="portal-login-email" />
      <PasswordField value={pwd} onChange={setPwd} placeholder="Password" testId="portal-login-password" autoComplete="current-password" />
      {err && <div className="text-sm bg-red-500/20 border border-red-400/40 rounded-md p-2" data-testid="portal-password-error">{err}</div>}
      <button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-white text-[#0F172A] font-bold flex items-center justify-center gap-2 hover:bg-emerald-50 disabled:opacity-60" data-testid="portal-password-submit">
        <KeyRound className="w-4 h-4" /> {busy ? "Signing in…" : "Sign in with password"}</button>
    </form>
  );
}

export function ChangePassword({ forced, onDone, onCancel }) {
  const [cur, setCur] = useState("");
  const [n1, setN1] = useState("");
  const [n2, setN2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (n1 !== n2) { setErr("The two new passwords differ"); return; }
    setBusy(true); setErr("");
    try { await portalAxios.post(`${PORTAL_API}/auth/change-password`, { current_password: cur, new_password: n1 }, { headers: hdr() }); onDone(); }
    catch (ex) { setErr(errText(ex, "Could not change the password")); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#0F172A] text-white flex items-center justify-center p-6" data-testid="portal-change-password">
      <form onSubmit={submit} className="w-full max-w-sm bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur space-y-3">
        <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-[0.3em]"><KeyRound className="w-4 h-4" /> {forced ? "First sign-in" : "Change password"}</div>
        <h1 className="text-2xl font-black">{forced ? "Choose your own password" : "New password"}</h1>
        {forced && <p className="text-sm text-white/70">You signed in with a temporary password. Please set a personal one now — at least 8 characters with upper- and lower-case letters and a number.</p>}
        <PasswordField value={cur} onChange={setCur} placeholder={forced ? "Temporary password" : "Current password"} testId="portal-cp-current" autoComplete="current-password" />
        <PasswordField value={n1} onChange={setN1} placeholder="New password" testId="portal-cp-new" autoComplete="new-password" />
        <PasswordField value={n2} onChange={setN2} placeholder="Repeat new password" testId="portal-cp-repeat" autoComplete="new-password" />
        {err && <div className="text-sm bg-red-500/20 border border-red-400/40 rounded-md p-2" data-testid="portal-cp-error">{err}</div>}
        <button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-emerald-400 text-[#0F172A] font-bold hover:bg-emerald-300 disabled:opacity-60" data-testid="portal-cp-submit">{busy ? "Saving…" : "Save password"}</button>
        {!forced && <button type="button" onClick={onCancel} className="w-full text-sm text-white/60 hover:text-white" data-testid="portal-cp-cancel">Cancel</button>}
      </form>
    </div>
  );
}
